export interface DisplayConditions {
    questionId: number;
    optionId: number;
}

/**
 * Explanatory text attached to a question.
 *
 * `label` is the upstream `prompt` and is genuinely absent on most questions: in the v4 NO template only the PEP
 * question carries one. A missing label means the text is a short inline hint ("Flere svaralternativer er mulig")
 * rather than the trigger copy for a "read more" dialog.
 */
export interface Info {
    label?: string | null;
    description: string;
}

export interface Options {
    id: number;
    label: string;
}

export interface Question {
    groupId?: string;
    name: number;
    label: string;
    type: string;
    options?: Options[] | null;
    info?: Info | null;
    displayCondition?: DisplayConditions | null;
}

export enum KycQuestionTypes {
    COUNTRY_CODE_SELECTION = 'COUNTRY_CODE_SELECTION',
    /**
     * Introduced by the know-your-customer-service v4 onboarding template, where the citizenship follow-up ("which
     * country or countries?") collects several countries in one question instead of one per country slot.
     */
    MULTIPLE_COUNTRY_CODE_SELECTION = 'MULTIPLE_COUNTRY_CODE_SELECTION',
    FREE_TEXT = 'FREE_TEXT',
    MULTIPLE_SELECTION = 'MULTIPLE_SELECTION',
    SINGLE_SELECTION = 'SINGLE_SELECTION',
    CHECK_BOX = 'CHECK_BOX',
    /**
     * Unused by the v4 NO template, which asks "flere statsborgerskap?" as a SINGLE_SELECTION. Kept because the
     * templates for the other markets have not been checked.
     */
    BUTTON = 'BUTTON',
    SINGLE_SELECTION_DROPDOWN = 'SINGLE_SELECTION_DROPDOWN',
}

/**
 * Whether the UI has a field for this question type.
 *
 * A question of an unknown type renders no input, so it is required, unanswerable and blocks the page. The pages use
 * this to say something rather than leaving the customer on a dead "next" button; KycQuestion reports the type to
 * Sentry.
 */
export const isSupportedQuestionType = (type: string): boolean =>
    (Object.values(KycQuestionTypes) as string[]).includes(type);

export const KycButton = {
    SELECTED: 1,
    UNSELECTED: 2,
};

/**
 * The citizenship group is the one group whose id the UI has to know: its questions are kept on a single page, and the
 * NO market pre-selects Norway on its country question.
 */
export const CITIZENSHIP_GROUP_ID = 'CITIZENSHIP';

export interface Survey {
    id: string;
    title: string;
    type: string;
    questions: Question[];
}

export interface KycCountries {
    id: string;
    label: string;
}

export interface Kyc {
    groups: Survey[];
    kycCountries?: KycCountries[];
    /**
     * The know-your-customer-service survey template these questions came from. It has to be echoed back with the
     * answers: v4 resolves the survey template by it and silently discards answers whose question id that template
     * does not contain, so sending the wrong one loses answers behind a 200.
     */
    templateVersion?: number;
}

export type KycAnswer = {
    category: string;
    question: string;
    answer: string;
};

export type KycDetails = {
    kycAnswers: KycAnswer[];
    templateVersion?: number;
};

export type KnowYourCustomerRequest = {
    kycDetails: KycDetails;
};
